import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import CardPromotion from '@/components/sections/builder/elements/body/cart-total/component/card-promotion';
import { Button } from '@/components/ui/button';
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useMutateValidateVoucher } from '@/hooks/api/storefront/promotion';
import { cn, isEmpty, parseErrorMessage } from '@/lib/utils';
import { ThemePromotionType } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { HttpTypes } from '@/types';
import { BasePromotion } from '@/types/http/promotion/common';
import { isAxiosError } from 'axios';
import { ArrowLeft, Circle, Plus, TicketPercent, XIcon } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';

type VoucherListProps = {
  cart: HttpTypes.StoreCart;
  drawerView: 'cart' | 'voucher' | 'gift';
  setDrawerView: React.Dispatch<
    React.SetStateAction<'cart' | 'voucher' | 'gift' | 'active_offers'>
  >;
  openModal: boolean;
  token?: string;
  listPromotions?: BasePromotion[];
  isLoadingListPromotions: boolean;
  payloadListPromotion: {
    items: {
      item_id: string;
      qty: number;
      amount: number;
    }[];
    grand_total: number;
    current_date: string;
  };
  setErrorVoucher: React.Dispatch<React.SetStateAction<string | null>>;
  errorVoucher: string | null;
  voucherCode: string;
  setVoucherCode: React.Dispatch<React.SetStateAction<string>>;
  onApplyVoucher: (selectedVoucherIds: string[]) => void;
  onRedeemVoucher: (promo: BasePromotion[], code: string) => void;
  promotionTheme?: ThemePromotionType;
};

export default function VoucherList({
  openModal,
  setDrawerView,
  token,
  listPromotions,
  isLoadingListPromotions,
  payloadListPromotion,
  voucherCode,
  setVoucherCode,
  setErrorVoucher,
  errorVoucher,
  onApplyVoucher,
  onRedeemVoucher,
  promotionTheme,
}: VoucherListProps) {
  const inputId = useId();

  const [selectedPromo, setSelectedPromo] = useState<string[]>([]);

  const selectedPromotion = usePromotionStore((s) => s.selectedPromotion);

  const validateVoucher = useMutateValidateVoucher({
    onSuccess: (data) => {
      const promoRewards = data.data?.promotion_rewards;
      const isValid = data.data?.is_valid;

      if (isValid && promoRewards?.length) {
        setVoucherCode('');
        setErrorVoucher(null);
        onRedeemVoucher(promoRewards, voucherCode);
      } else {
        setErrorVoucher('Voucher is not valid');
      }
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        setErrorVoucher(error.response?.data?.message);
      }
    },
  });

  const handleOnSubmitRedeem = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      const code = voucherCode.trim();
      if (!code) {
        setErrorVoucher('Please enter the voucher code');
        return;
      }

      if ((selectedPromotion ?? []).some((p) => p.voucher_code === code)) {
        setErrorVoucher('Voucher code already used');
        return;
      }

      const payload = {
        voucher_code: code,
        order: {
          items: payloadListPromotion?.items?.map((item) => ({
            item_id: item.item_id,
            qty: item.qty,
          })),
          grand_total: payloadListPromotion.grand_total || 0,
        },
      };

      try {
        await validateVoucher.mutateAsync({
          body: payload,
          token: token as string,
        });
      } catch (err) {
        if (isAxiosError(err)) {
          const rawMessage = err.response?.data?.data ?? err.response?.data?.message;

          try {
            const parsedMessage = JSON.parse(rawMessage);
            setErrorVoucher(
              parsedMessage.message || 'An error occurred while processing the voucher'
            );
          } catch {
            setErrorVoucher(
              parseErrorMessage(rawMessage) || 'An error occurred while processing the voucher'
            );
          }
        } else {
          setErrorVoucher('An unexpected error occurred. Please try again.');
        }
      }
    },
    [token, voucherCode, payloadListPromotion, setErrorVoucher, validateVoucher, onRedeemVoucher]
  );

  const handleSelectPromo = (promoId: string, isOverlap: boolean) => {
    const isSelected = selectedPromo.includes(promoId);

    if (isSelected) {
      setSelectedPromo((prev) => prev.filter((id) => id !== promoId));
      return;
    }

    if (!isOverlap) {
      setSelectedPromo([promoId]);
    } else {
      setSelectedPromo((prev) => [...prev, promoId]);
    }
  };

  const isPromoDisabled = (promo: BasePromotion): boolean => {
    const alreadySelected = selectedPromo.includes(promo.promotion_id);
    if (alreadySelected) return false;

    const selectedPromotionsObjects = (listPromotions ?? []).filter((p) =>
      selectedPromo.includes(p.promotion_id)
    );

    const hasNonStackableSelected = selectedPromotionsObjects.some((p) => !p.is_overlap);
    const hasStackableSelected = selectedPromotionsObjects.some((p) => p.is_overlap);

    if (payloadListPromotion.grand_total < promo.minimum_spending) {
      return true;
    }

    if (!promo.is_overlap && hasNonStackableSelected) {
      return true;
    }

    if (promo.is_overlap && hasNonStackableSelected) {
      return true;
    }

    if (!promo.is_overlap && hasStackableSelected) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (openModal) {
      const selectedIds = (selectedPromotion ?? []).map((promo) => promo.promotion_id);
      setSelectedPromo(selectedIds);
    } else {
      setSelectedPromo([]);
    }
  }, [openModal, selectedPromotion]);

  useEffect(() => {
    if (errorVoucher) {
      const timeout = setTimeout(() => {
        setErrorVoucher(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [errorVoucher]);

  return (
    <>
      <DrawerHeader className='flex items-center justify-between text-left'>
        <div className='flex items-center'>
          <Button variant={null} onClick={() => setDrawerView('cart')} size='icon'>
            <ArrowLeft className='theme_cart_drawer_color h-[18px] w-[18px]' />
          </Button>
          <DrawerTitle className='theme_cart_drawer_color'>Select vouchers</DrawerTitle>
        </div>
        <DrawerClose onClick={() => setDrawerView('cart')}>
          <XIcon className='h-4 w-4' />
        </DrawerClose>
      </DrawerHeader>

      <div
        className='px-4'
        style={
          {
            '--text-primary': promotionTheme?.colorScheme?.text || '#ffffff',
            '--text-secondary': promotionTheme?.colorScheme?.textSecondary || '#ffffff',
            '--background': promotionTheme?.colorScheme?.background || '#000000',
            '--btn-label': promotionTheme?.colorScheme?.btnSolidlabel || '#000000',
            '--btn-background': promotionTheme?.colorScheme?.btnSolidBackgroud || '#FFFFFF',
          } as React.CSSProperties
        }
      >
        <form className='flex flex-col gap-2' onSubmit={handleOnSubmitRedeem}>
          <div className='flex items-center gap-2'>
            <label htmlFor={inputId} className='sr-only'>
              Voucher Code
            </label>
            <input
              type='text'
              placeholder='Enter voucher code'
              className={cn(
                'w-full flex-1 rounded px-3 py-2.5 text-xs outline-none ring-1 focus:ring-1',
                errorVoucher ? 'border-red-500' : 'ring-gray-200 focus:ring-[var(--background)]'
              )}
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value)}
            />

            <Button
              type='submit'
              size='sm'
              variant={isEmpty(promotionTheme?.colorScheme) ? 'secondary' : null}
              className={cn(
                'items-center rounded-sm py-5 text-xs font-normal',
                !isEmpty(promotionTheme?.colorScheme) &&
                  'bg-[var(--btn-background)] text-[var(--btn-label)] transition-all duration-300 hover:opacity-80'
              )}
              disabled={validateVoucher.isPending}
            >
              {validateVoucher.isPending && <LoadingAnimationIcon size={4} />} Redeem
            </Button>
          </div>
          {errorVoucher && <p className='mt-1 text-xs text-red-500'>{errorVoucher}</p>}
        </form>
      </div>

      <ScrollArea className='h-full overflow-y-auto px-4 pt-4' type='scroll'>
        {isLoadingListPromotions ? (
          <div className='flex flex-col gap-1.5'>
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className='h-20 w-full' />
            ))}
          </div>
        ) : (
          <div className='flex flex-col gap-1.5'>
            {(listPromotions ?? []).length > 0 ? (
              listPromotions?.map((promo) => {
                const isSelected = selectedPromo.includes(promo.promotion_id);
                const isDisabled = isPromoDisabled(promo);
                const percentage = Math.min(
                  (payloadListPromotion.grand_total / promo.minimum_spending) * 100,
                  100
                );
                const isEligiblePromo = payloadListPromotion.grand_total >= promo.minimum_spending;

                return (
                  <CardPromotion
                    key={promo.promotion_id}
                    promo={promo}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    percentage={percentage}
                    isEligiblePromo={isEligiblePromo}
                    grandTotal={payloadListPromotion.grand_total}
                    handleSelectPromo={handleSelectPromo}
                    theme={promotionTheme}
                  />
                );
              })
            ) : (
              <div className='flex flex-col items-center justify-center p-6 text-center'>
                <div className='relative mb-10'>
                  <div className='relative'>
                    <TicketPercent className='h-16 w-24 text-gray-400' strokeWidth={0.6} />

                    <Circle className='absolute -right-1 -top-1 h-4 w-4 text-gray-300' />
                    <Circle className='absolute -bottom-1 -left-1 h-3 w-3 text-gray-400' />
                    <Plus className='absolute -left-2 top-2 h-3 w-3 text-gray-300' />
                    <Circle className='absolute -top-2 left-2 h-2 w-2 fill-current text-gray-300' />
                    <Plus className='absolute -bottom-2 right-2 h-2 w-2 fill-current text-gray-400' />
                  </div>
                </div>

                <h3 className='mb-1 text-base font-medium text-foreground'>
                  No vouchers available
                </h3>
                <p className='text-sm text-muted-foreground'>
                  Enter voucher code in the column above
                </p>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      <DrawerFooter
        className='px-4'
        style={
          {
            '--text-secondary': promotionTheme?.colorScheme?.textSecondary || '#ffffff',
          } as React.CSSProperties
        }
      >
        {selectedPromo.length > 0 && (
          <div className='flex items-center justify-center gap-2 py-1'>
            <p className='text-sm text-[--text-secondary]'>
              {selectedPromo.length} voucher selected
            </p>
          </div>
        )}
        <Button
          variant='secondary'
          onClick={() => {
            onApplyVoucher(selectedPromo);
            setDrawerView('cart');
          }}
          className='theme_button theme_cart_drawer_btn w-full items-center px-24 underline-offset-2'
        >
          Use Voucher
        </Button>
      </DrawerFooter>
    </>
  );
}
